import {Appointment} from '@app/shared/interfaces/appointment.interface';

export type AppointmentExportFormat = 'csv' | 'xlsx';

export interface AppointmentExportOptions {
  title: string;
  showReferringProvider: boolean;
  showStatus: boolean;
  missingDetailIds?: ReadonlySet<string>;
}

interface AppointmentExportColumn {
  header: string;
  width: number;
  value: (appointment: Appointment) => string;
}

export async function exportAppointmentResults(
  appointments: Appointment[],
  format: AppointmentExportFormat,
  options: AppointmentExportOptions
): Promise<void> {
  const columns = getExportColumns(options);
  const fileName = getExportFileName(options.title);

  if (format === 'csv') {
    const rows = [
      columns.map((column) => escapeCsvValue(column.header)).join(','),
      ...appointments.map((appointment) => columns
        .map((column) => escapeCsvValue(column.value(appointment)))
        .join(','))
    ];
    const csv = `\uFEFF${rows.join('\r\n')}`;
    downloadFile(
      new Blob([csv], {type: 'text/csv;charset=utf-8'}),
      `${fileName}.csv`
    );
    console.log(`Exported ${appointments.length} appointments.`);
    return;
  }

  const {default: ExcelJS} = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Appointments');

  worksheet.addRow(columns.map((column) => column.header));
  appointments.forEach((appointment) => {
    worksheet.addRow(columns.map((column) => column.value(appointment)));
  });
  worksheet.columns.forEach((column, index) => {
    column.width = columns[index].width;
  });
  worksheet.views = [{state: 'frozen', ySplit: 1}];
  worksheet.autoFilter = {
    from: {row: 1, column: 1},
    to: {row: 1, column: columns.length}
  };
  worksheet.getRow(1).font = {bold: true};
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {argb: 'FF6EC6AA'}
  };

  const buffer = await workbook.xlsx.writeBuffer();
  downloadFile(
    new Blob([new Uint8Array(buffer)], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }),
    `${fileName}.xlsx`
  );
  console.log(`Exported ${appointments.length} appointments.`);
}

function getExportColumns(options: AppointmentExportOptions): AppointmentExportColumn[] {
  const columns: AppointmentExportColumn[] = [
    {header: 'Appointment Date', width: 18, value: (appointment) => appointment.date ?? ''},
    {header: 'Appointment Time', width: 18, value: (appointment) => appointment.time ?? ''},
    {header: 'Patient Name', width: 28, value: (appointment) => getFullName(appointment.patient)},
    {header: 'DOB', width: 16, value: (appointment) => appointment.patient?.dateOfBirth ?? ''},
    {header: 'Iowa Ortho Provider', width: 28, value: (appointment) => getFullName(appointment.iowaOrthoProvider)},
    {header: 'Appointment Location', width: 28, value: (appointment) => appointment.location ?? ''},
    {header: 'Visit', width: 24, value: (appointment) => appointment.type ?? ''}
  ];

  if (options.showReferringProvider) {
    columns.splice(6, 0, {
      header: 'Referring Provider',
      width: 28,
      value: (appointment) => getFullName(appointment.referringProvider)
    });
  }
  if (options.showStatus) {
    columns.push({header: 'Status', width: 18, value: (appointment) => appointment.status ?? ''});
  }

  return columns;
}

function getFullName(person: {firstName: string; lastName: string} | null | undefined): string {
  return [person?.firstName, person?.lastName].filter(Boolean).join(' ');
}

function escapeCsvValue(value: string): string {
  const spreadsheetSafeValue = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${spreadsheetSafeValue.replace(/"/g, '""')}"`;
}

function getExportFileName(title: string): string {
  const normalizedTitle = (title || 'appointments')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${normalizedTitle || 'appointments'}-${new Date().toISOString().slice(0, 10)}`;
}

function downloadFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
